// Web mirror of the Android recurring/notification domain. Kept dependency-free
// and pure so it matches `Recurrence.kt` and `NotificationLogData.idFor` exactly
// — the two platforms MUST agree or the cross-client dedup breaks.

export const RECURRENCE_TYPES = ["DAILY", "WEEKLY", "MONTHLY"];

export const TASK_STATE_BACKLOG = "task_state_backlog";
export const TASK_STATE_WIP = "task_state_wip";

export const REMOTE_STATE_UPLOAD = "remote_state_upload";
export const REMOTE_STATE_DELETE = "remote_state_delete";
export const REMOTE_STATE_SYNCED = "remote_state_synced";

export const NOTIFICATION_TYPE = {
  DUE_DATE: "DUE_DATE",
  POMODORO: "POMODORO",
  RECURRING_CREATED: "RECURRING_CREATED",
};

// Java Calendar weekday constants: SUNDAY=1 .. SATURDAY=7. Android stores
// `dayOfWeek` in this convention, so we keep it and convert to JS (0=Sun..6=Sat)
// only when doing date math.
export const CAL_SUNDAY = 1;
export const CAL_SATURDAY = 7;

/**
 * Deterministic positive Int32 id for a dedup key — the EXACT algorithm of
 * Android's `NotificationLogData.idFor`: Java String.hashCode (h = 31*h + c,
 * 32-bit overflow via Math.imul), then & 0x7fffffff, then map 0 -> 1.
 *
 * Dedup keys by type:
 *   DUE_DATE          -> "due-<taskId>-<dueDate>"
 *   RECURRING_CREATED -> "rec-<ruleId>-<fireSlotMillis>"
 *   POMODORO          -> "pom-<firedAt>"
 */
export function notificationId(key) {
  let h = 0;
  for (let i = 0; i < key.length; i++) {
    h = (Math.imul(31, h) + key.charCodeAt(i)) | 0;
  }
  const positive = h & 0x7fffffff;
  return positive === 0 ? 1 : positive;
}

function daysInMonth(year, monthIndex) {
  // monthIndex 0..11; day 0 of next month = last day of this month.
  return new Date(year, monthIndex + 1, 0).getDate();
}

function clampDayOfMonth(date, dayOfMonth) {
  const max = daysInMonth(date.getFullYear(), date.getMonth());
  return Math.max(1, Math.min(dayOfMonth, max));
}

/**
 * Next fire instant strictly AFTER `afterMillis`. Mirror of
 * `Recurrence.nextFireAfter` (local time, same strict-greater-than semantics).
 *
 * @param {"DAILY"|"WEEKLY"|"MONTHLY"} type
 * @param {number} dayOfWeek  Java Calendar SUNDAY..SATURDAY (1..7) — WEEKLY only
 * @param {number} dayOfMonth 1..28 (clamped to month length) — MONTHLY only
 * @param {number} hour 0..23
 * @param {number} minute 0..59
 * @param {number} afterMillis
 * @returns {number} epoch millis
 */
export function nextFireAfter(type, dayOfWeek, dayOfMonth, hour, minute, afterMillis) {
  const cal = new Date(afterMillis);
  cal.setHours(hour, minute, 0, 0);

  if (type === "DAILY") {
    if (cal.getTime() <= afterMillis) cal.setDate(cal.getDate() + 1);
  } else if (type === "WEEKLY") {
    const targetJsDay = (dayOfWeek - 1 + 7) % 7; // Java 1..7 (Sun..Sat) -> JS 0..6
    while (cal.getDay() !== targetJsDay || cal.getTime() <= afterMillis) {
      cal.setDate(cal.getDate() + 1);
    }
  } else {
    // MONTHLY
    cal.setDate(clampDayOfMonth(cal, dayOfMonth));
    cal.setHours(hour, minute, 0, 0);
    if (cal.getTime() <= afterMillis) {
      cal.setMonth(cal.getMonth() + 1, 1); // go to the 1st of next month first
      cal.setDate(clampDayOfMonth(cal, dayOfMonth));
      cal.setHours(hour, minute, 0, 0);
    }
  }
  return cal.getTime();
}

const WEEKDAY_NAMES = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

/** Locale-ish weekday name for a Java Calendar dayOfWeek (1=Sun..7=Sat). */
export function weekdayName(dayOfWeek) {
  return WEEKDAY_NAMES[(dayOfWeek - 1 + 7) % 7] || "";
}

export function recurrenceLabel(type) {
  return { DAILY: "Daily", WEEKLY: "Weekly", MONTHLY: "Monthly" }[type] || type;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** One-line human summary, mirroring Android's recurrenceSummary. */
export function recurrenceSummary(rule) {
  const time = `${pad2(rule.hour)}:${pad2(rule.minute)}`;
  if (rule.recurrenceType === "DAILY") return `Daily · ${time}`;
  if (rule.recurrenceType === "WEEKLY") return `${weekdayName(rule.dayOfWeek)} · ${time}`;
  return `Day ${rule.dayOfMonth} · ${time}`;
}

export function formatDateTime(millis) {
  if (!millis || millis <= 0) return "—";
  const d = new Date(millis);
  const month = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()];
  return `${month} ${d.getDate()} · ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function notificationIcon(type) {
  if (type === "DUE_DATE") return "⏰";
  if (type === "POMODORO") return "🍅";
  if (type === "RECURRING_CREATED") return "🔁";
  return "🔔";
}
